// src/components/university/marketplace/CourseCard.js
import React from 'react';
import { Link } from 'react-router-dom';
import { FiStar, FiUsers, FiClock, FiVideo, FiFileText, FiPlayCircle } from 'react-icons/fi';
import { formatPriceCents } from '../../../services/university/universityConstants';
import FlagBadge from '../../common/FlagBadge';

const typeIcon = {
  on_demand: <FiPlayCircle size={14} />,
  live_webinar: <FiVideo size={14} />,
  document: <FiFileText size={14} />
};
const typeLabel = {
  on_demand: 'On demand',
  live_webinar: 'En vivo',
  document: 'Libro / documento'
};

const CourseCard = ({ course }) => {
  if (!course) return null;
  const detailPath = `/universidad/curso/${course.slug}`;
  const durationMin = Number(course.estimated_duration_minutes || 0);
  const durationHrs = durationMin >= 60 ? `${Math.round(durationMin / 60)} h` : `${durationMin || 0} min`;

  return (
    <Link
      to={detailPath}
      className="group bg-dark-800 border border-dark-700 hover:border-primary-500 rounded-xl overflow-hidden transition-all flex flex-col"
    >
      <div className="relative aspect-video bg-dark-700 overflow-hidden">
        {course.cover_image_url ? (
          <img
            src={course.cover_image_url}
            alt={course.title}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-500 text-sm">
            Sin portada
          </div>
        )}
        <div className="absolute top-3 left-3 flex items-center gap-2">
          <span className="bg-dark-900/80 backdrop-blur text-gray-200 text-xs px-2 py-1 rounded-full inline-flex items-center gap-1">
            {typeIcon[course.course_type]}
            {typeLabel[course.course_type] || 'Curso'}
          </span>
          {course.is_featured && (
            <span className="bg-primary-600/90 text-white text-xs px-2 py-1 rounded-full font-medium">
              Destacado
            </span>
          )}
        </div>
        {course.is_free && (
          <span className="absolute top-3 right-3 bg-emerald-500/95 text-white text-xs font-semibold px-2 py-1 rounded-full">
            Gratis
          </span>
        )}
      </div>

      <div className="p-4 flex-1 flex flex-col">
        <h3 className="font-semibold text-white line-clamp-2 group-hover:text-primary-400 transition-colors min-h-[3em]">
          {course.title}
        </h3>
        {course.subtitle && (
          <p className="text-gray-400 text-sm mt-1 line-clamp-2">{course.subtitle}</p>
        )}

        <div className="mt-3 flex items-center gap-2 text-sm">
          {course.instructor_avatar_url ? (
            <img
              src={course.instructor_avatar_url}
              alt={course.instructor_display_name}
              className="w-6 h-6 rounded-full object-cover"
            />
          ) : (
            <div className="w-6 h-6 rounded-full bg-primary-700 text-white flex items-center justify-center text-xs">
              {(course.instructor_display_name || '?').charAt(0).toUpperCase()}
            </div>
          )}
          <span className="text-gray-300 truncate">{course.instructor_display_name || 'Instructor'}</span>
          {course.instructor_country && (
            <FlagBadge country={course.instructor_country} size="xs" title={course.instructor_country} />
          )}
        </div>

        <div className="mt-3 flex items-center justify-between text-xs text-gray-400">
          <div className="flex items-center gap-3">
            {Number(course.average_rating) > 0 && (
              <span className="inline-flex items-center gap-1 text-amber-400">
                <FiStar size={12} className="fill-current" />
                {Number(course.average_rating).toFixed(1)}
                <span className="text-gray-500">({course.total_reviews})</span>
              </span>
            )}
            <span className="inline-flex items-center gap-1">
              <FiUsers size={12} /> {course.total_students || 0}
            </span>
            {course.course_type !== 'document' && (
              <span className="inline-flex items-center gap-1">
                <FiClock size={12} /> {durationHrs}
              </span>
            )}
          </div>
        </div>

        <div className="mt-4 pt-3 border-t border-dark-700 flex items-center justify-between">
          <span className="text-white font-bold">
            {course.is_free
              ? 'Gratis'
              : formatPriceCents(course.price_amount_cents, course.price_currency || 'USD')}
          </span>
          <span className="text-primary-400 text-sm font-medium group-hover:text-primary-300">
            Ver curso
          </span>
        </div>
      </div>
    </Link>
  );
};

export default CourseCard;
